import { useState } from 'react';
import {
  Box, Typography, Collapse, IconButton, CircularProgress, Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { colors, cardSx } from '../theme/designTheme';

const statusCfg = {
  pass: { color: colors.low,      bg: colors.lowBg,      label: 'PASS', Icon: CheckCircleIcon },
  warn: { color: colors.medium,   bg: colors.mediumBg,   label: 'WARN', Icon: WarningAmberIcon },
  fail: { color: colors.critical, bg: colors.criticalBg, label: 'FAIL', Icon: ErrorOutlineIcon },
};

const sourceLabel = {
  abn:         'ABN / ABR',
  asic:        'ASIC',
  kyc:         'KYC / CDD',
  application: 'Application',
  credit:      'Credit',
  valuation:   'Valuation',
};

const PayloadView = ({ payload }) => {
  if (!payload || typeof payload !== 'object') return null;
  return (
    <Box sx={{ mt: 1, pl: 4, pr: 2, pb: 1.5 }}>
      <Box sx={{
        bgcolor: colors.surfaceElevated, border: `1px solid ${colors.borderSubtle}`,
        borderRadius: 1, p: 1.5,
      }}>
        {Object.entries(payload).map(([k, v]) => (
          <Box key={k} sx={{ display: 'flex', gap: 2, py: 0.5, alignItems: 'flex-start' }}>
            <Typography sx={{
              color: colors.textMuted, fontFamily: 'Lexend', fontSize: '10px',
              fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              minWidth: 160, flexShrink: 0,
            }}>
              {k.replace(/_/g, ' ')}
            </Typography>
            <Typography sx={{
              color: k === 'warning' ? colors.medium : colors.textSecondary,
              fontFamily: 'Lexend', fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const VerificationRow = ({ v }) => {
  const [open, setOpen] = useState(false);
  const cfg = statusCfg[v.status] || statusCfg.warn;
  const Icon = cfg.Icon;

  return (
    <Box sx={{
      borderTop: `1px solid ${colors.borderSubtle}`,
      '&:first-of-type': { borderTop: 'none' },
    }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
          cursor: 'pointer', transition: 'background 0.15s',
          '&:hover': { bgcolor: colors.surfaceHover },
        }}
      >
        <Icon sx={{ color: cfg.color, fontSize: 20, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em' }}>
              {sourceLabel[v.source] || v.source.toUpperCase()}
            </Typography>
            <Box sx={{ px: 0.75, py: 0.1, bgcolor: cfg.bg, borderRadius: '3px' }}>
              <Typography sx={{ color: cfg.color, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em' }}>
                {cfg.label}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontSize: '13px', fontWeight: 600, lineHeight: 1.2 }}>
            {v.title}
          </Typography>
          {v.summary && (
            <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '11px', mt: 0.4, lineHeight: 1.4 }}>
              {v.summary}
            </Typography>
          )}
        </Box>
        {v.source_url && (
          <Link
            href={v.source_url} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ color: colors.accent, display: 'flex', alignItems: 'center' }}
          >
            <OpenInNewIcon sx={{ fontSize: 15 }} />
          </Link>
        )}
        <IconButton size="small" sx={{ color: colors.textMuted }}>
          <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', fontSize: 18 }} />
        </IconButton>
      </Box>
      <Collapse in={open}>
        <PayloadView payload={v.payload} />
      </Collapse>
    </Box>
  );
};

const IndependentVerificationPanel = ({ verifications, summary, isLoading }) => {
  return (
    <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${colors.borderSubtle}`, flexShrink: 0 }}>
        <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontWeight: 700, fontSize: '13px' }}>
          Independent Verification
        </Typography>
        {summary && (
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {['pass', 'warn', 'fail'].map((s) => {
              const c = statusCfg[s];
              return (
                <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, bgcolor: c.bg, borderRadius: '3px' }}>
                  <Typography sx={{ color: c.color, fontFamily: 'Lexend', fontSize: '10px', fontWeight: 700 }}>
                    {summary[s] ?? 0}
                  </Typography>
                  <Typography sx={{ color: c.color, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em' }}>
                    {c.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: colors.accent }} />
          </Box>
        ) : !verifications || verifications.length === 0 ? (
          <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '12px', px: 2, py: 3, textAlign: 'center' }}>
            No verification checks recorded.
          </Typography>
        ) : (
          verifications.map((v) => <VerificationRow key={v.id} v={v} />)
        )}
      </Box>
    </Box>
  );
};

export default IndependentVerificationPanel;
